import {
  DEFECT_IMAGE_LAUNCHABLE_ID,
  SENSOR_CARLINE_LAUNCHABLE_ID,
  VIDEO_AUGMENTATION_LAUNCHABLE_ID,
} from "./pipelines";

const readLaunchableId = (value: string | undefined) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export const DEFAULT_OPENCLAW_LAUNCHABLE_ID = "env-3B2Oju9FqUDqcZtHZckFQTS9MJh";
export const DEFAULT_HERMES_LAUNCHABLE_ID = "env-3EJslHqi8ugW91RBgD3erRCQEJ7";

const configuredOpenClawLaunchableId = readLaunchableId(
  import.meta.env.VITE_OPENCLAW_LAUNCHABLE_ID,
);
const configuredHermesLaunchableId = readLaunchableId(
  import.meta.env.VITE_HERMES_LAUNCHABLE_ID,
);
const shouldUseDefaultOpenClawLaunchable =
  import.meta.env.VITE_STAGE === "prod";
const shouldUseDefaultHermesLaunchable = import.meta.env.VITE_STAGE === "prod";

export const AGENT_LAUNCHABLE_IDS = {
  openclaw:
    configuredOpenClawLaunchableId ??
    (shouldUseDefaultOpenClawLaunchable
      ? DEFAULT_OPENCLAW_LAUNCHABLE_ID
      : undefined),
  nemoclaw: readLaunchableId(import.meta.env.VITE_NEMOCLAW_LAUNCHABLE_ID),
  hermes:
    configuredHermesLaunchableId ??
    (shouldUseDefaultHermesLaunchable
      ? DEFAULT_HERMES_LAUNCHABLE_ID
      : undefined),
} as const;

export const PHYSICAL_AI_AGENT_LAUNCHABLE_IDS = {
  sensorCarline: SENSOR_CARLINE_LAUNCHABLE_ID,
  defectImage: DEFECT_IMAGE_LAUNCHABLE_ID,
  videoAugmentation: VIDEO_AUGMENTATION_LAUNCHABLE_ID,
} as const;

export const DEFAULT_OPENCLAW_DEPLOY_URL = `https://brev.nvidia.com/launchable/deploy/now?launchableID=${encodeURIComponent(
  DEFAULT_OPENCLAW_LAUNCHABLE_ID,
)}`;
export const DEFAULT_HERMES_DEPLOY_URL = `https://brev.nvidia.com/launchable/deploy/now?launchableID=${encodeURIComponent(
  DEFAULT_HERMES_LAUNCHABLE_ID,
)}`;

const AGENT_LAUNCHABLE_ID_SET = new Set(
  [
    ...Object.values(AGENT_LAUNCHABLE_IDS),
    ...Object.values(PHYSICAL_AI_AGENT_LAUNCHABLE_IDS),
  ].filter((launchableId): launchableId is string => Boolean(launchableId)),
);

export const getAgentLaunchableDeployHref = (launchableId?: string) =>
  launchableId
    ? `/launchable/deploy?launchableID=${encodeURIComponent(launchableId)}`
    : undefined;

export const getAgentLaunchableDeployNowHref = (launchableId?: string) =>
  launchableId
    ? `/launchable/deploy/now?launchableID=${encodeURIComponent(launchableId)}`
    : undefined;

export const isAgentLaunchableRoute = (pathname: string, search: string) => {
  if (
    pathname !== "/launchable/deploy" &&
    pathname !== "/launchable/deploy/now"
  ) {
    return false;
  }

  const launchableId = new URLSearchParams(search).get("launchableID");
  return !!launchableId && AGENT_LAUNCHABLE_ID_SET.has(launchableId);
};

export const isNemoClawRoute = (pathname: string, search: string) => {
  if (pathname === "/nemoclaw") {
    return true;
  }

  if (
    pathname !== "/launchable/deploy" &&
    pathname !== "/launchable/deploy/now"
  ) {
    return false;
  }

  const launchableId = new URLSearchParams(search).get("launchableID");
  return !!launchableId && launchableId === AGENT_LAUNCHABLE_IDS.nemoclaw;
};

export const isAgentsRoute = (pathname: string, search: string) =>
  pathname === "/agents" ||
  pathname === "/nemoclaw" ||
  pathname === "/physical-ai" ||
  isAgentLaunchableRoute(pathname, search);
